/**
 * Image pre/post-processing. This is the part we actually own -- the model gives
 * a coarse mask, and everything here is what turns that into a usable cutout.
 *
 * Runs inside the worker, so OffscreenCanvas is used for all resampling (it is
 * hardware-accelerated and far better quality than hand-rolled bilinear).
 */
function canvas(w, h) {
    const c = new OffscreenCanvas(w, h);
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx)
        throw new Error('2D canvas context unavailable');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    return { c, ctx };
}
/** Draw a bitmap at an explicit size and read back its pixels. */
export function rasterise(bmp, w, h) {
    const { ctx } = canvas(w, h);
    ctx.drawImage(bmp, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    return { data: img.data, width: w, height: h };
}
/**
 * Cap the longest edge while preserving aspect ratio. maxEdge <= 0 means
 * "leave it alone" (the HD tier exports at source resolution).
 */
export function capSize(w, h, maxEdge) {
    if (maxEdge <= 0)
        return { w, h };
    const longest = Math.max(w, h);
    if (longest <= maxEdge)
        return { w, h };
    const k = maxEdge / longest;
    return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}
/**
 * Build the NCHW float32 tensor the model expects.
 *
 * letterbox=true  : fit inside the square, pad the remainder with zeros, and
 *                   report the content size so the mask can be cropped back.
 * letterbox=false : squash to the square (BiRefNet is trained this way).
 */
export function toTensor(bmp, size, mean, std, letterbox) {
    let sw = size;
    let sh = size;
    if (letterbox) {
        const k = Math.min(size / bmp.width, size / bmp.height);
        sw = Math.max(1, Math.round(bmp.width * k));
        sh = Math.max(1, Math.round(bmp.height * k));
    }
    const { ctx } = canvas(size, size);
    // Padding is zeros in *normalised* space, which is what the exported graph
    // was given during conversion. Fill black first, then draw.
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(bmp, 0, 0, sw, sh);
    const { data } = ctx.getImageData(0, 0, size, size);
    const plane = size * size;
    const tensor = new Float32Array(3 * plane);
    for (let i = 0, p = 0; i < plane; i++, p += 4) {
        tensor[i] = (data[p] / 255 - mean[0]) / std[0];
        tensor[plane + i] = (data[p + 1] / 255 - mean[1]) / std[1];
        tensor[2 * plane + i] = (data[p + 2] / 255 - mean[2]) / std[2];
    }
    return { tensor, fit: { sw, sh } };
}
const sigmoid = (x) => 1 / (1 + Math.exp(-x));
/**
 * Turn the raw model output into an 8-bit alpha map at the model's resolution.
 *
 * Applies U2Net-style min-max normalisation, which meaningfully improves
 * contrast on low-confidence predictions. Guarded so a near-flat map (an image
 * with no clear subject) does not get its noise amplified into garbage.
 */
export function maskToAlpha(raw, w, h, needsSigmoid) {
    const n = w * h;
    const vals = new Float32Array(n);
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < n; i++) {
        const v = needsSigmoid ? sigmoid(raw[i]) : raw[i];
        vals[i] = v;
        if (v < min)
            min = v;
        if (v > max)
            max = v;
    }
    const span = max - min;
    const stretch = span > 0.1 && span < 1.6;
    const out = new Uint8ClampedArray(n);
    for (let i = 0; i < n; i++) {
        let v = vals[i];
        if (stretch)
            v = (v - min) / span;
        out[i] = v * 255;
    }
    return out;
}
/**
 * Scale an alpha map up to the output size. Goes through a canvas so we get
 * smooth bilinear interpolation rather than blocky nearest-neighbour edges.
 *
 * `fit` crops away letterbox padding before scaling.
 */
export function resampleAlpha(alpha, srcW, srcH, fit, dstW, dstH) {
    // Pack the single-channel map into an opaque greyscale image.
    const rgba = new Uint8ClampedArray(srcW * srcH * 4);
    for (let i = 0, p = 0; i < srcW * srcH; i++, p += 4) {
        rgba[p] = rgba[p + 1] = rgba[p + 2] = alpha[i];
        rgba[p + 3] = 255;
    }
    const src = canvas(srcW, srcH);
    src.ctx.putImageData(new ImageData(rgba, srcW, srcH), 0, 0);
    const dst = canvas(dstW, dstH);
    // Crop to the letterboxed content region, then stretch to the output size.
    dst.ctx.drawImage(src.c, 0, 0, fit.sw, fit.sh, 0, 0, dstW, dstH);
    const out = new Uint8ClampedArray(dstW * dstH);
    const { data } = dst.ctx.getImageData(0, 0, dstW, dstH);
    for (let i = 0, p = 0; i < dstW * dstH; i++, p += 4)
        out[i] = data[p];
    return out;
}
/**
 * Remap alpha so the transition band is tighter.
 *
 * Segmentation models leave a wide low-confidence ramp that reads as a grey
 * halo once composited. Pulling the ramp in removes the haze while leaving
 * genuinely soft pixels (hair, motion blur) partially transparent.
 */
export function tightenEdges(alpha, lo = 0.04, hi = 0.92) {
    const span = hi - lo;
    const lut = new Uint8ClampedArray(256);
    for (let v = 0; v < 256; v++) {
        lut[v] = Math.min(1, Math.max(0, (v / 255 - lo) / span)) * 255;
    }
    for (let i = 0; i < alpha.length; i++)
        alpha[i] = lut[alpha[i]];
}
/**
 * Colour decontamination -- the step that actually removes halos.
 *
 * A semi-transparent edge pixel is a blend of the subject and the background we
 * just deleted, so it still carries the old background's colour. Compositing it
 * over white or a new backdrop shows up as a fringe.
 *
 * We replace the colour of every partial pixel with a distance-weighted average
 * of nearby fully-opaque pixels, which propagates true foreground colour
 * outward. Alpha is left untouched, so softness is preserved.
 *
 * Only the thin partial-alpha band is touched, so cost stays low even at
 * full resolution.
 */
export function decontaminate(rgba, alpha, w, h, radius = 3) {
    const SOLID = 250; // treat as pure foreground
    const FLOOR = 8; // below this there is nothing worth recolouring
    const src = new Uint8ClampedArray(rgba); // sample from the original
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x;
            const a = alpha[i];
            if (a >= SOLID || a <= FLOOR)
                continue;
            let r = 0;
            let g = 0;
            let b = 0;
            let wsum = 0;
            const y0 = Math.max(0, y - radius);
            const y1 = Math.min(h - 1, y + radius);
            const x0 = Math.max(0, x - radius);
            const x1 = Math.min(w - 1, x + radius);
            for (let ny = y0; ny <= y1; ny++) {
                for (let nx = x0; nx <= x1; nx++) {
                    const ni = ny * w + nx;
                    if (alpha[ni] < SOLID)
                        continue;
                    const dx = nx - x;
                    const dy = ny - y;
                    const wgt = 1 / (1 + dx * dx + dy * dy);
                    const p = ni * 4;
                    r += src[p] * wgt;
                    g += src[p + 1] * wgt;
                    b += src[p + 2] * wgt;
                    wsum += wgt;
                }
            }
            if (wsum === 0)
                continue; // isolated wisp, nothing reliable to borrow
            const p = i * 4;
            // Blend toward the clean colour proportionally to how transparent we are:
            // nearly-solid pixels keep their own colour, faint ones take the borrowed one.
            const mix = 1 - a / SOLID;
            rgba[p] = src[p] * (1 - mix) + (r / wsum) * mix;
            rgba[p + 1] = src[p + 1] * (1 - mix) + (g / wsum) * mix;
            rgba[p + 2] = src[p + 2] * (1 - mix) + (b / wsum) * mix;
        }
    }
}
/** Write the alpha channel into an RGBA buffer, in place. */
export function applyAlpha(rgba, alpha) {
    for (let i = 0, p = 3; i < alpha.length; i++, p += 4)
        rgba[p] = alpha[i];
}
